/**
 * PDP image zoom lightbox — click/tap to open; pinch, wheel, and pan inside.
 */
(function () {
  'use strict';

  var MIN_SCALE = 1;
  var MAX_SCALE = 4;
  var DOUBLE_TAP_MS = 300;
  var DOUBLE_TAP_SCALE = 2.5;

  var dialog = null;
  var backdrop = null;
  var viewport = null;
  var stage = null;
  var imgEl = null;
  var closeBtn = null;
  var activePointers = new Map();
  var scale = 1;
  var translateX = 0;
  var translateY = 0;
  var baseFitW = 0;
  var baseFitH = 0;
  var pinchStartDistance = 0;
  var pinchStartScale = 1;
  var panStartX = 0;
  var panStartY = 0;
  var panOriginX = 0;
  var panOriginY = 0;
  var lastTapTime = 0;
  var lastTapX = 0;
  var lastTapY = 0;
  var slideHandlers = [];
  var savedScrollY = 0;
  var isZoomOpen = false;
  var viewportPointerMoved = false;
  var MOBILE_TIP_KEY = 'tcn_pdp_zoom_tip_seen';
  var PHONE_MAX = 767;
  var TILES_MIN = 992;
  var mobileZoomBtn = null;
  var mobileZoomTrack = null;
  var tilesZoomBtn = null;
  var tilesZoomTrack = null;
  var tilesActiveSlide = null;
  var zoomSlides = [];
  var zoomIndex = 0;
  var prevBtn = null;
  var nextBtn = null;
  var counterEl = null;
  var navSwipeStartX = 0;
  var navSwipeStartY = 0;
  var SWIPE_NAV_THRESHOLD = 56;

  function isMobileGallery() {
    return window.innerWidth <= PHONE_MAX;
  }

  function isTilesViewport() {
    return window.innerWidth >= TILES_MIN;
  }

  function isTilesTrack(track) {
    if (!track) return false;
    return (
      track.classList.contains('tcn-pdp-tiles-grid') ||
      track.getAttribute('data-tcn-pdp-layout') === 'tiles'
    );
  }

  function isCarouselDesktop() {
    return !isMobileGallery() && !(isTilesViewport() && document.querySelector('.gallery-control--tiles'));
  }

  function mobileTipWasSeen() {
    try {
      return window.localStorage.getItem(MOBILE_TIP_KEY) === '1';
    } catch (err) {
      return false;
    }
  }

  function markMobileTipSeen() {
    try {
      window.localStorage.setItem(MOBILE_TIP_KEY, '1');
    } catch (err) {
      /* private browsing */
    }
    dismissMobileZoomButton();
  }

  function getActiveSlide(track) {
    if (!track) return null;
    var slides = track.querySelectorAll('.tcn-product-slide--image');
    if (!slides.length) return null;
    var w = track.clientWidth || 1;
    var i = Math.round(track.scrollLeft / w);
    i = Math.max(0, Math.min(slides.length - 1, i));
    return slides[i];
  }

  function removeSlideZoomIcons(track) {
    if (!track) return;
    track.querySelectorAll('.tcn-pdp-zoom-icon:not(.tcn-pdp-zoom-icon--mobile):not(.tcn-pdp-zoom-icon--tiles-fab)').forEach(function (el) {
      el.remove();
    });
  }

  function dismissDesktopTilesZoomButton() {
    document.querySelectorAll('.tcn-pdp-zoom-icon--tiles-fab').forEach(function (el) {
      el.remove();
    });
    tilesZoomBtn = null;
    tilesZoomTrack = null;
    tilesActiveSlide = null;
  }

  function dismissMobileZoomButton() {
    if (mobileZoomBtn && mobileZoomBtn.parentNode) {
      mobileZoomBtn.parentNode.removeChild(mobileZoomBtn);
    }
    mobileZoomBtn = null;
    document.querySelectorAll('.tcn-pdp-zoom-icon--mobile').forEach(function (el) {
      el.remove();
    });
    if (mobileZoomTrack) {
      removeSlideZoomIcons(mobileZoomTrack);
    }
    mobileZoomTrack = null;
    document.querySelectorAll('.tcn-pdp-mobile-zoom-tip').forEach(function (el) {
      el.remove();
    });
  }

  function getFirstImageSlide(track) {
    if (!track) return null;
    return track.querySelector('.tcn-product-slide--image');
  }

  function getTilesZoomSlide(track) {
    if (tilesActiveSlide && track && track.contains(tilesActiveSlide)) {
      return tilesActiveSlide;
    }
    return getFirstImageSlide(track);
  }

  /** One zoom button for the tile grid on large desktop. */
  function injectDesktopTilesZoomButton(track) {
    if (!track || !isTilesTrack(track) || !isTilesViewport()) {
      dismissDesktopTilesZoomButton();
      return;
    }

    tilesZoomTrack = track;
    removeSlideZoomIcons(track);
    dismissDesktopTilesZoomButton();

    /* Retail PDP: per-image .tcn-pdp-zoom-icon (shown on hover) */
    if (document.querySelector('[data-retail-product]')) {
      track.querySelectorAll('.tcn-product-slide--image').forEach(function (slide) {
        var icon = ensureSlideZoomIcon(slide);
        function onIconClick(e) {
          e.preventDefault();
          e.stopPropagation();
          openZoomFromSlide(slide);
        }
        icon.addEventListener('click', onIconClick);
        slideHandlers.push({ slide: slide, icon: icon, onIconClick: onIconClick });
      });
      return;
    }

    track.style.position = track.style.position || 'relative';

    var host = track.closest('.gallery-control--tiles') || track;
    if (host !== track) {
      host.style.position = host.style.position || 'relative';
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tcn-pdp-zoom-icon tcn-pdp-zoom-icon--tiles-fab';
    btn.setAttribute('aria-label', 'Zoom in on this photo');

    function onTilesZoomClick(e) {
      e.preventDefault();
      e.stopPropagation();
      var slide = getTilesZoomSlide(track);
      if (slide) openZoomFromSlide(slide);
    }

    btn.addEventListener('click', onTilesZoomClick);
    host.appendChild(btn);
    tilesZoomBtn = btn;

    slideHandlers.push({
      isTilesFab: true,
      icon: btn,
      onIconClick: onTilesZoomClick
    });
  }

  /** One zoom button for the whole gallery on mobile. */
  function injectMobileZoomButton(track) {
    if (!track || !isMobileGallery()) {
      dismissMobileZoomButton();
      return;
    }

    mobileZoomTrack = track;
    removeSlideZoomIcons(track);

    document.querySelectorAll('.tcn-pdp-zoom-icon--mobile').forEach(function (el) {
      el.remove();
    });

    var host = track.closest('.pdp-gallery-scroll-area') || track.parentElement;
    if (!host) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tcn-pdp-zoom-icon tcn-pdp-zoom-icon--mobile';
    btn.setAttribute('aria-label', 'Zoom in on this photo');

    function onMobileZoomClick(e) {
      e.preventDefault();
      e.stopPropagation();
      var slide = getActiveSlide(track);
      if (slide) openZoomFromSlide(slide);
    }

    btn.addEventListener('click', onMobileZoomClick);
    host.appendChild(btn);
    mobileZoomBtn = btn;

    slideHandlers.push({
      isMobileFab: true,
      icon: btn,
      onIconClick: onMobileZoomClick
    });
  }

  function pointHitsImage(clientX, clientY) {
    if (!stage) return false;
    var r = stage.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  }

  function updateZoomedClass() {
    if (!viewport) return;
    viewport.classList.toggle('is-zoomed', scale > 1.05);
  }

  function ensureDialog() {
    if (dialog) return;

    dialog = document.createElement('dialog');
    dialog.className = 'tcn-pdp-zoom';
    dialog.setAttribute('aria-label', 'Enlarged product image');
    dialog.innerHTML =
      '<button type="button" class="tcn-pdp-zoom__close" aria-label="Close enlarged image">' +
      '<svg class="tcn-pdp-zoom__close-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M7 7l10 10M17 7L7 17" stroke="currentColor" stroke-width="1.65" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<button type="button" class="tcn-pdp-zoom__nav tcn-pdp-zoom__nav--prev" aria-label="Previous image">' +
      '<svg class="tcn-pdp-zoom__nav-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M14.5 6.5L9 12l5.5 5.5" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<button type="button" class="tcn-pdp-zoom__nav tcn-pdp-zoom__nav--next" aria-label="Next image">' +
      '<svg class="tcn-pdp-zoom__nav-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M9.5 6.5L15 12l-5.5 5.5" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<p class="tcn-pdp-zoom__counter" aria-live="polite"></p>' +
      '<div class="tcn-pdp-zoom__backdrop" aria-hidden="true"></div>' +
      '<div class="tcn-pdp-zoom__viewport">' +
      '<div class="tcn-pdp-zoom__stage">' +
      '<img class="tcn-pdp-zoom__img" alt="" decoding="async" draggable="false">' +
      '</div></div>';

    document.body.appendChild(dialog);
    backdrop = dialog.querySelector('.tcn-pdp-zoom__backdrop');
    viewport = dialog.querySelector('.tcn-pdp-zoom__viewport');
    stage = dialog.querySelector('.tcn-pdp-zoom__stage');
    imgEl = dialog.querySelector('.tcn-pdp-zoom__img');
    closeBtn = dialog.querySelector('.tcn-pdp-zoom__close');
    prevBtn = dialog.querySelector('.tcn-pdp-zoom__nav--prev');
    nextBtn = dialog.querySelector('.tcn-pdp-zoom__nav--next');
    counterEl = dialog.querySelector('.tcn-pdp-zoom__counter');

    closeBtn.addEventListener('click', closeZoom);
    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      navigateZoom(-1);
    });
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      navigateZoom(1);
    });
    backdrop.addEventListener('click', closeZoom);
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeZoom();
    });
    dialog.addEventListener('close', function () {
      resetTransform();
      unlockPageScroll();
      document.removeEventListener('keydown', onZoomKeyDown);
    });

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
  }

  function measureBaseFit() {
    if (!viewport || !imgEl) return;
    var vw = viewport.clientWidth;
    var vh = viewport.clientHeight;
    var iw = imgEl.naturalWidth;
    var ih = imgEl.naturalHeight;
    if (!iw || !ih) {
      baseFitW = vw;
      baseFitH = vh;
    } else {
      var fit = Math.min(vw / iw, vh / ih);
      baseFitW = iw * fit;
      baseFitH = ih * fit;
    }
    imgEl.style.width = baseFitW + 'px';
    imgEl.style.height = baseFitH + 'px';
  }

  function releaseAllPointerCapture() {
    if (!viewport) return;
    activePointers.forEach(function (_, id) {
      if (viewport.hasPointerCapture && viewport.hasPointerCapture(id)) {
        try {
          viewport.releasePointerCapture(id);
        } catch (err) {
          /* already released */
        }
      }
    });
    activePointers.clear();
    pinchStartDistance = 0;
  }

  function lockPageScroll() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('tcn-pdp-zoom-open');
    document.body.classList.add('tcn-pdp-zoom-open');
  }

  function unlockPageScroll() {
    isZoomOpen = false;
    releaseAllPointerCapture();

    document.documentElement.classList.remove('tcn-pdp-zoom-open');
    document.body.classList.remove('tcn-pdp-zoom-open');

    document.documentElement.style.overflow = '';
    document.documentElement.style.height = '';
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.touchAction = '';

    requestAnimationFrame(function () {
      window.scrollTo(0, savedScrollY);
    });
  }

  function clearTransition() {
    if (stage) stage.style.transition = '';
  }

  function setTransition() {
    if (stage) stage.style.transition = 'transform 0.22s cubic-bezier(0.25, 0.1, 0.25, 1)';
  }

  function resetTransform() {
    scale = 1;
    translateX = 0;
    translateY = 0;
    pinchStartDistance = 0;
    clearTransition();
    applyTransform();
  }

  function isDialogOpen() {
    return dialog && (dialog.open || dialog.hasAttribute('open'));
  }

  function applyTransform() {
    if (!stage) return;
    stage.style.transform =
      'translate3d(' + translateX + 'px,' + translateY + 'px,0) scale(' + scale + ')';
    updateZoomedClass();
  }

  function clampPan() {
    if (!viewport) return;
    var vw = viewport.clientWidth;
    var vh = viewport.clientHeight;
    if (!baseFitW || !baseFitH) measureBaseFit();
    var dw = baseFitW * scale;
    var dh = baseFitH * scale;
    var maxX = Math.max(0, (dw - vw) / 2);
    var maxY = Math.max(0, (dh - vh) / 2);
    translateX = Math.max(-maxX, Math.min(maxX, translateX));
    translateY = Math.max(-maxY, Math.min(maxY, translateY));
  }

  /** Keep the image pixel under the finger fixed while scale changes. */
  function zoomAtClientPoint(clientX, clientY, newScale) {
    if (!viewport) return;

    var vRect = viewport.getBoundingClientRect();
    var mx = clientX - vRect.left;
    var my = clientY - vRect.top;
    var vw = viewport.clientWidth;
    var vh = viewport.clientHeight;

    var contentX = (mx - vw / 2 - translateX) / scale;
    var contentY = (my - vh / 2 - translateY) / scale;

    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

    translateX = mx - vw / 2 - contentX * scale;
    translateY = my - vh / 2 - contentY * scale;

    clampPan();
    applyTransform();
  }

  function getDistance() {
    var pts = Array.from(activePointers.values());
    if (pts.length < 2) return 0;
    var dx = pts[1].x - pts[0].x;
    var dy = pts[1].y - pts[0].y;
    return Math.hypot(dx, dy);
  }

  function getMidpointClient() {
    var pts = Array.from(activePointers.values());
    if (pts.length < 2) return { x: pts[0].x, y: pts[0].y };
    return {
      x: (pts[0].x + pts[1].x) / 2,
      y: (pts[0].y + pts[1].y) / 2
    };
  }

  function onPointerDown(e) {
    if (!isDialogOpen()) return;
    if (e.target === backdrop || e.target === closeBtn) return;

    if (e.target === viewport && !pointHitsImage(e.clientX, e.clientY)) {
      closeZoom();
      return;
    }

    viewportPointerMoved = false;
    clearTransition();
    navSwipeStartX = e.clientX;
    navSwipeStartY = e.clientY;
    viewport.setPointerCapture(e.pointerId);
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.size === 2) {
      pinchStartDistance = getDistance();
      pinchStartScale = scale;
    } else if (activePointers.size === 1 && scale > 1) {
      panStartX = e.clientX;
      panStartY = e.clientY;
      panOriginX = translateX;
      panOriginY = translateY;
    }
  }

  function onPointerMove(e) {
    if (!activePointers.has(e.pointerId)) return;
    var prev = activePointers.get(e.pointerId);
    if (prev && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) > 4) {
      viewportPointerMoved = true;
    }
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.size >= 2 && pinchStartDistance > 0) {
      var dist = getDistance();
      var mid = getMidpointClient();
      var nextScale = pinchStartScale * (dist / pinchStartDistance);
      zoomAtClientPoint(mid.x, mid.y, nextScale);
      return;
    }

    if (activePointers.size === 1 && scale > 1) {
      translateX = panOriginX + (e.clientX - panStartX);
      translateY = panOriginY + (e.clientY - panStartY);
      clampPan();
      applyTransform();
    }
  }

  function onPointerUp(e) {
    if (viewport && viewport.hasPointerCapture && viewport.hasPointerCapture(e.pointerId)) {
      try {
        viewport.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* already released */
      }
    }

    activePointers.delete(e.pointerId);
    if (activePointers.size < 2) {
      pinchStartDistance = 0;
    }

    if (activePointers.size > 0) return;

    if (scale <= 1.05 && zoomSlides.length > 1 && viewportPointerMoved) {
      var dx = e.clientX - navSwipeStartX;
      var dy = e.clientY - navSwipeStartY;
      if (Math.abs(dx) > SWIPE_NAV_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.2) {
        navigateZoom(dx < 0 ? 1 : -1);
        lastTapTime = 0;
        return;
      }
    }

    if (!viewportPointerMoved && !pointHitsImage(e.clientX, e.clientY)) {
      closeZoom();
      return;
    }

    var now = Date.now();
    var isDoubleTap =
      now - lastTapTime < DOUBLE_TAP_MS &&
      Math.hypot(e.clientX - lastTapX, e.clientY - lastTapY) < 28;

    if (isDoubleTap) {
      setTransition();
      if (scale > 1.05) {
        resetTransform();
      } else {
        zoomAtClientPoint(e.clientX, e.clientY, DOUBLE_TAP_SCALE);
      }
      lastTapTime = 0;
      return;
    }

    lastTapTime = now;
    lastTapX = e.clientX;
    lastTapY = e.clientY;
  }

  function onZoomKeyDown(e) {
    if (!isZoomOpen || zoomSlides.length <= 1) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      navigateZoom(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      navigateZoom(1);
    }
  }

  function collectZoomSlides(track) {
    if (!track) return [];
    var list = [];
    track.querySelectorAll('.tcn-product-slide--image').forEach(function (slide) {
      var url = getZoomUrl(slide);
      if (!url) return;
      var img = slide.querySelector('img');
      list.push({
        url: url,
        alt: img ? img.alt : '',
        slide: slide
      });
    });
    return list;
  }

  function findZoomSlideIndex(slide) {
    for (var i = 0; i < zoomSlides.length; i++) {
      if (zoomSlides[i].slide === slide) return i;
    }
    return 0;
  }

  function updateZoomNav() {
    if (!prevBtn || !nextBtn || !counterEl) return;
    var total = zoomSlides.length;
    var showNav = total > 1;
    prevBtn.hidden = !showNav;
    nextBtn.hidden = !showNav;
    counterEl.hidden = !showNav;
    if (!showNav) {
      counterEl.textContent = '';
      return;
    }
    counterEl.textContent = zoomIndex + 1 + ' / ' + total;
  }

  function showZoomImage(item, animate) {
    if (!item || !imgEl) return;
    resetTransform();

    function applyImage() {
      imgEl.alt = item.alt || '';
      imgEl.onload = function () {
        if (!isZoomOpen) return;
        measureBaseFit();
        resetTransform();
        if (stage) {
          stage.classList.remove('is-changing');
        }
      };
      imgEl.src = item.url;
      if (imgEl.complete && imgEl.naturalWidth > 0) {
        imgEl.onload();
      }
    }

    if (animate && stage) {
      stage.classList.add('is-changing');
      window.setTimeout(applyImage, 120);
    } else {
      applyImage();
    }
  }

  function navigateZoom(delta) {
    if (!zoomSlides.length || zoomSlides.length <= 1) return;
    var nextIndex = zoomIndex + delta;
    if (nextIndex < 0) nextIndex = zoomSlides.length - 1;
    if (nextIndex >= zoomSlides.length) nextIndex = 0;
    zoomIndex = nextIndex;
    showZoomImage(zoomSlides[zoomIndex], true);
    updateZoomNav();
  }

  function openZoomAtIndex(index) {
    if (!zoomSlides.length) return;
    var safeIndex = index;
    if (safeIndex < 0) safeIndex = 0;
    if (safeIndex >= zoomSlides.length) safeIndex = zoomSlides.length - 1;
    zoomIndex = safeIndex;
    showZoomImage(zoomSlides[zoomIndex], false);
    updateZoomNav();
  }

  function openZoom(url, alt) {
    if (!url || isZoomOpen) return;
    zoomSlides = [{ url: url, alt: alt || '', slide: null }];
    zoomIndex = 0;
    ensureDialog();
    unlockPageScroll();
    openZoomAtIndex(0);
    lockPageScroll();
    isZoomOpen = true;
    document.addEventListener('keydown', onZoomKeyDown);
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function closeZoom() {
    if (!dialog || !isZoomOpen) return;
    isZoomOpen = false;
    document.removeEventListener('keydown', onZoomKeyDown);
    releaseAllPointerCapture();
    resetTransform();
    zoomSlides = [];
    zoomIndex = 0;

    if (isDialogOpen()) {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    unlockPageScroll();
  }

  function getZoomUrl(slide) {
    var img = slide.querySelector('img.tcn-slide-image, img[data-tcn-zoom-src]');
    if (!img) return null;
    return img.getAttribute('data-tcn-zoom-src') || img.currentSrc || img.getAttribute('src');
  }

  function openZoomFromSlide(slide) {
    if (isZoomOpen) {
      var track =
        slide.closest('.tcn-pdp-tiles-grid, .variant-slider, [data-tcn-pdp-track]') ||
        document.querySelector('.tcn-pdp-tiles-grid, .variant-slider[data-tcn-pdp-track]');
      zoomSlides = collectZoomSlides(track);
      if (!zoomSlides.length) return;
      zoomIndex = findZoomSlideIndex(slide);
      showZoomImage(zoomSlides[zoomIndex], true);
      updateZoomNav();
      return;
    }

    var track =
      slide.closest('.tcn-pdp-tiles-grid, .variant-slider, [data-tcn-pdp-track]') ||
      document.querySelector('.tcn-pdp-tiles-grid, .variant-slider[data-tcn-pdp-track]');
    zoomSlides = collectZoomSlides(track);
    if (!zoomSlides.length) return;

    zoomIndex = findZoomSlideIndex(slide);
    ensureDialog();
    unlockPageScroll();
    openZoomAtIndex(zoomIndex);
    lockPageScroll();
    isZoomOpen = true;
    document.addEventListener('keydown', onZoomKeyDown);
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function ensureSlideZoomIcon(slide) {
    var existing = slide.querySelector('.tcn-pdp-zoom-icon');
    if (existing && existing.tagName === 'BUTTON') return existing;
    if (existing) existing.remove();

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tcn-pdp-zoom-icon';
    btn.setAttribute('aria-label', 'Zoom in on this photo');
    slide.appendChild(btn);
    return btn;
  }

  function bindSlideOpen(slide, track) {
    if (!slide.classList.contains('tcn-product-slide--image')) return;

    var tilesMode = isTilesTrack(track) && isTilesViewport();
    var carouselDesktop = isCarouselDesktop() && !isTilesTrack(track);

    if (isMobileGallery()) return;

    var icon = null;

    if (carouselDesktop) {
      icon = ensureSlideZoomIcon(slide);

      function onIconClick(e) {
        e.preventDefault();
        e.stopPropagation();
        openZoomFromSlide(slide);
      }

      icon.addEventListener('click', onIconClick);
      slideHandlers.push({ slide: slide, icon: icon, onIconClick: onIconClick });
    }

    var activeId = null;
    var startX = 0;
    var startY = 0;
    var moved = false;

    function onDown(e) {
      if (icon && (e.target === icon || icon.contains(e.target))) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      activeId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      moved = false;
    }

    function onMove(e) {
      if (e.pointerId !== activeId) return;
      if (Math.hypot(e.clientX - startX, e.clientY - startY) > 12) moved = true;
    }

    function onUp(e) {
      if (e.pointerId !== activeId) return;
      activeId = null;
      if (moved) return;
      if (tilesMode) {
        tilesActiveSlide = slide;
      }
      openZoomFromSlide(slide);
    }

    slide.addEventListener('pointerdown', onDown);
    slide.addEventListener('pointermove', onMove);
    slide.addEventListener('pointerup', onUp);
    slide.addEventListener('pointercancel', onUp);

    slideHandlers.push({
      slide: slide,
      onDown: onDown,
      onMove: onMove,
      onUp: onUp
    });
  }

  function teardownZoom() {
    slideHandlers.forEach(function (h) {
      if (h.icon && h.onIconClick) {
        h.icon.removeEventListener('click', h.onIconClick);
      }
      if (h.onDown) {
        h.slide.removeEventListener('pointerdown', h.onDown);
        h.slide.removeEventListener('pointermove', h.onMove);
        h.slide.removeEventListener('pointerup', h.onUp);
        h.slide.removeEventListener('pointercancel', h.onUp);
      }
    });
    slideHandlers = [];
    dismissMobileZoomButton();
    dismissDesktopTilesZoomButton();
    closeZoom();
  }

  /** Remove legacy zoom labels. */
  function stripZoomLabels() {
    var selectors =
      '.tcn-pdp-zoom-hint, .tcn-pdp-zoom-hints, .tcn-pdp-zoom__help, ' +
      '.tcn-pdp-zoom-hint--desktop, .tcn-pdp-zoom-hint--mobile, ' +
      '.tcn-pdp-zoom__help--desktop, .tcn-pdp-zoom__help--mobile';
    document.querySelectorAll(selectors).forEach(function (el) {
      el.remove();
    });
  }

  function init(track) {
    if (!track) return;
    stripZoomLabels();
    teardownZoom();

    if (isMobileGallery()) {
      var carouselTrack = document.querySelector(
        '.gallery-control .js_prod_main.variant-slider.pdp-gallery-track'
      );
      if (carouselTrack) track = carouselTrack;
    }

    track.querySelectorAll('.tcn-pdp-zoom-icon:not(.tcn-pdp-zoom-icon--mobile):not(.tcn-pdp-zoom-icon--tiles-fab)').forEach(function (el) {
      el.remove();
    });

    var tilesMode = isTilesTrack(track) && isTilesViewport();

    track.querySelectorAll('.tcn-product-slide--image').forEach(function (slide) {
      bindSlideOpen(slide, track);
    });
    track.setAttribute('data-tcn-zoom-ready', 'true');

    if (isMobileGallery()) {
      injectMobileZoomButton(track);
    } else if (tilesMode) {
      injectDesktopTilesZoomButton(track);
    } else {
      dismissMobileZoomButton();
      dismissDesktopTilesZoomButton();
    }
  }

  window.TcnPdpZoom = {
    init: init,
    teardown: teardownZoom,
    open: openZoom,
    close: closeZoom,
    stripLabels: stripZoomLabels
  };

  stripZoomLabels();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stripZoomLabels);
  }
})();
